import { useContext, memo, useEffect, useState } from 'react';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { useDispatch } from 'react-redux';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';
import history from '@history';
import WdPageSimple from '@main/core/WdPageSimple';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Chip, CircularProgress, Grid, IconButton, Switch, Tooltip } from '@mui/material';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  BasicSelect,
  ButtonWithExpand,
  ComposedHeader,
  ComposedTable,
  InputSearch,
  Paginator,
} from '@main/components';
import Button from '@mui/material/Button';
import { useSimpleAlert } from '@main/hooks/useSimpleAlert';
import { showMessage } from 'app/store/core/messageSlice';
import { useDownloadBlob, useExtractSelector, useNativeDebounce, useRequest } from '@main/hooks';
import { formatDate } from 'src/app/utils/dates';
import _ from 'lodash';
import {
  catalogDbConnection,
  catalogVersions,
  exportFile,
  searchCompanies,
  statusCompany,
} from 'src/app/api/company';
import { typeDownloadEnum } from 'src/app/enums/fileEnum';
import { defaultsEnum } from 'src/app/enums/defaultsEnum';
import { customSwitchStyles } from 'app/styles/styled/customSwitchStyles';
import { useTheme } from '@mui/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { VersionInfoModal } from 'app/views/Companies/Components/VersionInfoModal/VersionInfoModal';
import InfoIcon from '@mui/icons-material/Info';
import { DbConnectionInfoModal } from 'app/views/Companies/Components/DbConnectionInfoModal/DbConnectionInfoModal';

const Root = styled(WdPageSimple)(({ theme }) => ({
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const exportFileOptions = _.omit(typeDownloadEnum, ['txt', 'zip']);

const statusAdminsData = [
  {
    label: 'Todo',
    value: '',
  },
  {
    label: 'Activo',
    value: 'true',
  },
  {
    label: 'Inactivo',
    value: 'false',
  },
];

const optionsMenu = [
  {
    label: 'Ver datos de facturación',
    value: 'billing',
  },
  {
    label: 'Historial pagos',
    value: 'pagos',
  },
  {
    label: 'Historial facturacion',
    value: 'historial',
  },
  {
    label: 'Renovación token',
    value: 'renovacion',
  },
];

const tableRowScheme = [
  {
    title: 'ID',
    minWidth: '80px',
  },
  {
    title: 'Nombre',
    minWidth: '240px',
    width: '240px',
  },
  {
    title: 'Correo',
    minWidth: '200px',
  },
  {
    title: 'Teléfono',
    minWidth: '180px',
  },
  {
    title: 'Clave',
    minWidth: '200px',
  },
  {
    title: 'Dominio',
    minWidth: '200px',
  },
  {
    title: 'IP',
    minWidth: '120px',
  },
  {
    title: 'Version',
    minWidth: '120px',
  },
  {
    title: 'Tipo de conexión de mongo',
    minWidth: '150px',
  },
  {
    title: 'Conexión de mongo',
    minWidth: '150px',
  },
  {
    title: 'Nivel',
    minWidth: '120px',
  },
  {
    title: 'Estatus',
    minWidth: '60px',
  },
  {
    title: 'Paso instalación',
    minWidth: '100px',
  },
  {
    title: 'Expiración de token',
    minWidth: '120px',
  },
  {
    title: 'Actualización',
    minWidth: '120px',
  },
  {
    title: 'Creación',
    minWidth: '120px',
  },
  {
    title: 'Opciones',
    minWidth: '156px',
    width: '156px',
    fixedRight: true,
  },
];

const IconInfo = styled(InfoIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.secondary.main,
  },
}));

const OptionButtons = memo(({ id, onUpdate, onView }) => {
  return (
    <div className="flex w-full flex-wrap flex-row justify-center items-center gap-10">
      <ButtonWithExpand
        className="whitespace-nowrap mx-4"
        variant="contained"
        color="primary"
        startIcon={<MenuIcon />}
      >
        {optionsMenu.map((option, index) => (
          <ButtonWithExpand.Item
            key={index}
            text={option.label}
            onClick={() => onView(option.value, id)}
          />
        ))}
      </ButtonWithExpand>

      <Tooltip title="Actualizar informacion de compañia">
        <span>
          <Button
            className="my-3"
            onClick={() => onUpdate(id)}
            size="small"
            variant="contained"
            color="secondary"
            sx={{ minWidth: '3.2rem', aspectRatio: 1 / 1, padding: '0rem' }}
          >
            <EditOutlinedIcon />
          </Button>
        </span>
      </Tooltip>
    </div>
  );
});

const handleUpdate = (id) => {
  console.log(id);
  history.push(`/companies/update?id=${id}`);
};

export const Companies = () => {
  const Dispatch = useDispatch();
  const { finalizeSession } = useContext(BootstrapContext);
  const openAlert = useSimpleAlert();
  // ------ States correspondiente a componentes de filtrado -------
  const [dataList, setDataList] = useState([]);
  const [querySearch, setQuerySearch] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(25);
  const [datePickerValue, setDatePickerValue] = useState([null, null]);

  // ------- Selects --------------
  const [adminStatus, setAdminStatus] = useState('');
  const [versionSelect, setVersionSelect] = useState('');
  const [dbConnectionSelect, setDbConnectionSelect] = useState('');

  const [showVersionModal, setShowVersionModal] = useState(false);
  const [versionSelected, setVersionSelected] = useState(null);
  const [showDbConnectionModal, setShowDbConnectionModal] = useState(false);
  const [dbConnectionSelected, setDbConnectionSelected] = useState(null);

  // ------ State general de formulario de buqueda ------------
  const [form, setForm] = useState({ query: '', initDate: '', endDate: '', paginate: undefined });

  // ----- Instancia de request que se usaran en la vista junto a sus valores de estado ---------
  const handleOnForbidden = async () => {
    await finalizeSession();
  };

  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };
  const {
    makeRequest: tryGetAdmins,
    isLoading,
    response,
  } = useRequest(searchCompanies, {
    onForbidden: handleOnForbidden,
    onError: handleOnError,
  });
  const { makeRequest: tryToggleStatusCompany } = useRequest(statusCompany);

  // ------ Catalogos ------
  const { dataSelect: versionDataSelect, dataIndex: versionsCatalog } = useExtractSelector(
    catalogVersions,
    {
      emptyOption: true,
      label: 'label',
      value: 'value',
    }
  );
  const { dataSelect: dbConnectionDataSelect, dataIndex: dbConnectionCatalog } = useExtractSelector(
    catalogDbConnection,
    {
      emptyOption: true,
      label: 'label',
      value: 'value',
    }
  );

  const theme = useTheme();

  const { triggerAction } = useNativeDebounce({
    delay: 600,
  });

  const {
    formats,
    tryDownload,
    progress: progressExport,
    isLoading: isLoadingExport,
  } = useDownloadBlob(exportFileOptions, exportFile);

  const handleExportFile = async (format) => {
    await tryDownload({
      type: format,
      name: 'Compañias',
    });
  };

  // ---- Borra todos los filtros ------
  const handleClearAllFilters = () => {
    setQuerySearch('');
    setAdminStatus('');
    setCurrentPage(undefined);
    setCurrentItemsPerPage(25);
    setDatePickerValue([null, null]);
  };

  const handleRequestGroups = () => {
    triggerAction().then((isOk) => {
      if (!isOk) return;
      tryGetAdmins(form, currentPage);
    });
  };

  // ----- Actualiza el formulario acorde cambio de filtros
  useEffect(() => {
    if (
      querySearch === undefined ||
      currentItemsPerPage === undefined ||
      datePickerValue === undefined
    )
      return;
    setForm((current) => {
      return {
        ...current,
        query: querySearch,
        paginate: currentItemsPerPage,
        initDate: datePickerValue[0] === null ? '' : datePickerValue[0],
        endDate: datePickerValue[1] === null ? '' : datePickerValue[1],

        // Otros filtros
        status: adminStatus === '' ? null : adminStatus.toString(),
        version: versionSelect === '' ? null : versionSelect.toString(),
        mongoDB: dbConnectionSelect === '' ? null : dbConnectionSelect.toString(),
      };
    });
  }, [
    querySearch,
    currentItemsPerPage,
    datePickerValue,
    adminStatus,
    versionSelect,
    dbConnectionSelect,
  ]);

  // ------ Actualiza la informacion de la tabla despues de la peticion -------
  useEffect(() => {
    if (!response) {
      return;
    }
    console.log('RESPONSE => ', response);
    setDataList(response.data);
    setTotalPages(response.last_page);
    setTotalItems(response.total);
    setCurrentPage(response.current_page);
  }, [response]);

  // -------- Realiza la peticion de datos al haber un cambio en el formulario -----
  useEffect(() => {
    if (form === undefined || form.paginate === undefined) return;
    console.log('FROM | QUERY => ', form);
    handleRequestGroups();
  }, [currentPage, form]);

  // ------ Inicializacion de la vista con toda la informacion disponible -------
  useEffect(() => {
    return () => {
      setDataList([]);
    };
  }, []);

  useEffect(() => {
    if (!versionSelect) {
      setVersionSelected(null);
      return;
    }
    const selected = versionsCatalog.find((i) => i.value === versionSelect);
    setVersionSelected(selected?.config || null);
  }, [versionsCatalog, versionSelect]);

  useEffect(() => {
    if (!dbConnectionSelect) {
      setDbConnectionSelected(null);
      return;
    }
    const selected = dbConnectionCatalog.find((i) => i.value === dbConnectionSelect);
    setDbConnectionSelected(selected?.config || null);
  }, [dbConnectionCatalog, dbConnectionSelect]);

  // ------ Funciones para actualizar los filtros ------
  const handleSearchQuery = (query = '') => {
    if (!query.length > 3) {
      return;
    }
    setQuerySearch(query);
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangeItems = (items) => {
    setCurrentItemsPerPage(items);
    setCurrentPage(1);
  };

  // ----- Acciones de botones en la tabla ----------
  const handleOpenSetup = () => {
    history.push('/setup');
  };

  const handleToggleStatus = async (id, status, name) => {
    const invertStatus = !status;
    openAlert({
      title: 'Atención',
      description: `Está seguro que desea ${
        status ? 'deshabilitar' : 'habilitar'
      } a la compañia ${name}?`,
    })
      .then((userResponse) => {
        if (!userResponse) {
          return;
        }
        tryToggleStatusCompany(id, { status: invertStatus }).then(({ isOk }) => {
          if (isOk) {
            Dispatch(
              showMessage({
                message: `Administrador ${status ? 'deshabilitado' : 'habilitado'} correctamente`,
                autoHideDuration: 3000,
              })
            );
            tryGetAdmins(form);
          }
        });
      })
      .catch((error) => console.log(error));
  };

  const handleCreateCompany = (companyId, companyStep) => {
    if (companyStep === undefined || companyStep === 'completed') return;
    history.push(`/setup?id=${companyId}&checkpoint=${companyStep}`);
  };

  const handleViewViews = (url, id) => {
    history.push(`/companies/info/${url}?id=${id}`);
  };

  return (
    <Root
      header={
        <ComposedHeader
          title="Compañías"
          description="Información general de compañías registradas"
        >
          <div className="px-4 py-4">
            <Button
              className="whitespace-nowrap mx-4"
              variant="contained"
              color="primary"
              onClick={() => handleOpenSetup()}
              startIcon={<BusinessOutlinedIcon />}
            >
              Agregar compañia
            </Button>
          </div>
          <div className="px-4 py-4">
            <ButtonWithExpand
              title={!isLoadingExport ? 'Descargar' : `Descargando ${progressExport}%`}
              className="whitespace-nowrap mx-4"
              variant="contained"
              color="success"
              startIcon={
                isLoadingExport ? (
                  <CircularProgress
                    color="inherit"
                    size="1.8rem"
                    variant={progressExport > 1 ? 'determinate' : 'indeterminate'}
                    value={progressExport}
                  />
                ) : (
                  <SimCardDownloadOutlinedIcon />
                )
              }
            >
              {formats.map((option, index) => (
                <ButtonWithExpand.Item
                  disabled={isLoadingExport}
                  key={`${index}_${option.value}_doc`}
                  text={`Documento ${option.label}`}
                  onClick={() => handleExportFile(option.value)}
                />
              ))}
            </ButtonWithExpand>
          </div>
        </ComposedHeader>
      }
      content={
        <Grid container className="px-32 pb-32 pt-0 flex flex-row w-full justify-center">
          <Grid item xs={12} md={12} className="flex w-full">
            <Card className="pb-24 px-24 mt-16 w-full">
              <CardContent className="flex w-full h-full flex-col justify-between">
                <div className="flex flex-col mt-24">
                  <Grid container className="w-full flex justify-center items-center py-8 mb-28">
                    <Grid item xs={12} md={7}>
                      <div className="flex flex-row flex-wrap justify-start items-center">
                        <div className="ml-4 mr-8 my-2 w-8/12 md:my-0">
                          <InputSearch
                            value={querySearch}
                            onChange={(query) => handleSearchQuery(query)}
                          />
                        </div>
                        <div className="ml-4 my-2 md:my-0">
                          <Button
                            fullWidth
                            size="medium"
                            variant="contained"
                            color="primary"
                            onClick={() => handleClearAllFilters()}
                          >
                            Limpiar filtros
                          </Button>
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <div className="flex flex-row justify-center md:justify-end items-center md:mt-0 mt-8">
                        <DateRangePicker
                          slotProps={{ textField: { size: 'small' } }}
                          localeText={{ start: 'Fecha inicial', end: 'Fecha final' }}
                          value={datePickerValue}
                          onChange={(current) => setDatePickerValue(current)}
                          disableFuture
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} className="p-2">
                    <Grid item xs={12} md={6} className="p-8 mb-24">
                      <div className="flex flex-row flex-wrap justify-start items-start gap-8">
                        <div>
                          <BasicSelect
                            data={versionDataSelect}
                            onChange={(data) => setVersionSelect(data)}
                            title="Version"
                            value={versionSelect}
                            size="small"
                          />
                          <IconButton
                            disabled={!versionSelect}
                            onClick={() => setShowVersionModal(true)}
                            className="ml-4"
                          >
                            <IconInfo />
                          </IconButton>
                        </div>
                        <div>
                          <BasicSelect
                            data={dbConnectionDataSelect}
                            onChange={(data) => setDbConnectionSelect(data)}
                            title="Conexión BD"
                            value={dbConnectionSelect}
                            size="small"
                            className="w-[14rem]"
                          />
                          <IconButton
                            disabled={!dbConnectionSelect}
                            onClick={() => setShowDbConnectionModal(true)}
                            className="ml-4"
                          >
                            <IconInfo />
                          </IconButton>
                        </div>
                        <BasicSelect
                          data={statusAdminsData}
                          onChange={(data) => setAdminStatus(data)}
                          title="Estatus"
                          value={adminStatus}
                          size="small"
                        />
                        {/*
                          Agrega mas selects
                        */}
                      </div>
                    </Grid>
                    <Grid item xs={12} md={6} className="p-8 mb-24">
                      <div className="flex flex-row justify-end">
                        <Paginator
                          totalPages={totalPages}
                          totalItems={totalItems}
                          onChangeCurrentPage={(page) => handleChangePage(page)}
                          onChangeItemsPerPage={(items) => handleChangeItems(items)}
                          currentPage={currentPage}
                          currentItemsPerPage={currentItemsPerPage}
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <div className="flex flex-col justify-center">
                    <ComposedTable
                      isLoading={isLoading}
                      className="w-full"
                      headers={tableRowScheme}
                      data={dataList}
                    >
                      <ComposedTable.Column
                        content={({ id }) => (
                          <Tooltip title={id}>
                            <span>{id.toString().substr(-8)}</span>
                          </Tooltip>
                        )}
                      />
                      <ComposedTable.Column
                        content={({ id, name }) => (
                          <Button
                            size="small"
                            className="font-normal transition-colors duration-100 ease-in-out hover:text-blue-500"
                            onClick={() => history.push(`/companies/info?id=${id}`)}
                          >
                            {name}
                          </Button>
                        )}
                      />
                      <ComposedTable.Column content={({ email }) => email} />
                      <ComposedTable.Column
                        content={({ phone, phoneCode }) =>
                          `${phoneCode ?? '+52'} ${phone ?? 'N/A'}`
                        }
                      />
                      <ComposedTable.Column content={({ key }) => key || 'N/A'} />
                      <ComposedTable.Column content={({ plesk }) => plesk?.domain || 'N/A'} />
                      <ComposedTable.Column content={({ plesk }) => plesk?.ip || 'N/A'} />
                      <ComposedTable.Column content={({ version }) => version || 'N/A'} />
                      <ComposedTable.Column content={({ mongoDB }) => mongoDB?.type || 'N/A'} />
                      <ComposedTable.Column
                        content={({ mongoDB }) => mongoDB?.connection || 'N/A'}
                      />
                      <ComposedTable.Column content={({ level }) => level || 'N/A'} />
                      <ComposedTable.Column
                        content={({ status, id }) =>
                          id ? (
                            <Switch
                              checked={status}
                              inputProps={{
                                'aria-label': 'Estatus',
                              }}
                              sx={customSwitchStyles(theme)}
                              onChange={() => handleToggleStatus(id, status)}
                            />
                          ) : (
                            defaultsEnum.labels.table
                          )
                        }
                      />
                      <ComposedTable.Column
                        content={({ id, step }) => (
                          <Chip
                            label={step}
                            color={step !== 'completed' ? 'secondary' : 'primary'}
                            variant="outlined"
                            size="small"
                            onClick={() => handleCreateCompany(id, step)}
                          />
                        )}
                      />
                      <ComposedTable.Column
                        content={({ token }) =>
                          token?.expires !== undefined ? formatDate(token.expires) : 'Sin datos'
                        }
                      />
                      <ComposedTable.Column
                        content={({ updated_at: updatedAt }) =>
                          updatedAt !== undefined ? formatDate(updatedAt) : 'Sin datos'
                        }
                      />
                      <ComposedTable.Column
                        content={({ created_at: createdAt }) =>
                          createdAt !== undefined ? formatDate(createdAt) : 'Sin datos'
                        }
                      />
                      <ComposedTable.Column
                        fixedRight
                        content={({ id, status, name }) => (
                          <OptionButtons
                            id={id}
                            onUpdate={() => handleUpdate(id)}
                            onView={handleViewViews}
                          />
                        )}
                      />
                    </ComposedTable>
                  </div>
                </div>
                <Grid container spacing={1} className="p-2 mt-24 flex">
                  <Grid item xs={12} md={12} className="py-8">
                    <div className="flex flex-row justify-end">
                      <Paginator
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onChangeCurrentPage={(page) => handleChangePage(page)}
                        onChangeItemsPerPage={(items) => handleChangeItems(items)}
                        currentPage={currentPage}
                        currentItemsPerPage={currentItemsPerPage}
                      />
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <VersionInfoModal
            title="Version del Repositorio"
            subtitle={versionSelect}
            show={showVersionModal}
            data={versionSelected}
            onHide={() => setShowVersionModal(false)}
          />
          <DbConnectionInfoModal
            title="Conexión MongoDB"
            subtitle={dbConnectionSelect}
            show={showDbConnectionModal}
            data={dbConnectionSelected}
            onHide={() => setShowDbConnectionModal(false)}
          />
        </Grid>
      }
    />
  );
};
